import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Seat from '../shared/Seat';
import HeroSeat from '../shared/HeroSeat';
import BottomControls from '../../layout/BottomControls/BottomControls';
import Question from '../../layout/BottomControls/Components/QuestionText/QuestionText';
import GameHeader from '../shared/GameHeader';
import useHandGenerator from '../../../components/HandGenerator';
import { useGame } from '../../../context/GameContext';
import { BET_TYPES } from '../types';
import { QuizResult } from '../../../analytics/QuizAnalytics';
import HandIntroAnimation from '../shared/HandIntroAnimation/HandIntroAnimation';

import './PreflopTable.css';

const POSITIONS = ['BTN', 'SB', 'BB', 'LJ', 'HJ', 'CO'];
const STARTING_STACK = 100;
const POSITION_ORDER = {
  'LJ': 0,
  'HJ': 1,
  'CO': 2,
  'BTN': 3,
  'SB': 4,
  'BB': 5
};

const isInPosition = (pos1, pos2) => {
  // Standard poker positions in order of acting
  const positionOrder = {
    'LJ': 0,
    'HJ': 1, 
    'CO': 2,
    'BTN': 3,
    'SB': 4,
    'BB': 5
  };
  
  // Handle invalid positions
  if (!pos1 || !pos2 || pos1 === pos2) {
    console.error(`Invalid position comparison: ${pos1} vs ${pos2}`);
    return false;
  }
  
  // Get numeric position values
  const pos1Value = positionOrder[pos1];
  const pos2Value = positionOrder[pos2];
  
  // For preflop 3bet sizing purposes, acting later = in position
  return pos1Value > pos2Value;
};

const validateGameConfig = (config) => {
  if (!config) {
    console.error('No game config provided');
    return false;
  }

  // Log the current config for debugging
  console.log('Validating game config:', config);

  // Required fields for all scenarios
  const baseRequiredFields = ['heroPosition', 'betType', 'totalHands'];
  let additionalFields = [];

  // Check for empty strings and convert to null
  Object.keys(config).forEach(key => {
    if (config[key] === '') {
      console.log(`Converting empty string to null for ${key}`);
      config[key] = null;
    }
  });

  // Determine required fields based on bet type
  switch (config.betType) {
    case BET_TYPES.OPEN:
      // For OPEN, we don't need additional position fields
      break;
    case BET_TYPES.FACING_OPEN_CALL:
    case BET_TYPES.FACING_OPEN_3BET:
      // For calls vs open and 3bets vs open, we need an opener position
      additionalFields.push('openPosition');
      break;
    case BET_TYPES.FACING_3BET_CALL:
      // For 3bet calls, we need both opener and 3bettor positions
      additionalFields.push('openPosition', 'threeBetPosition');
      break;
    case BET_TYPES.FACING_3BET_4BET:
      // For 4bets, we technically only need the 3bettor position
      // The opener position can be derived if missing
      additionalFields.push('threeBetPosition');
      break;
    default:
      // Unknown bet type, validate base fields only
      console.warn('Unknown bet type:', config.betType);
  }

  const requiredFields = [...baseRequiredFields, ...additionalFields];
  const missingFields = requiredFields.filter(field => {
    // Check if the field is missing or null
    return config[field] === undefined || config[field] === null;
  });
  
  if (missingFields.length > 0) {
    console.error('Missing required fields in game config:', missingFields);
    return false;
  }

  // Position validation based on bet type
  switch (config.betType) {
    case BET_TYPES.OPEN:
      // For OPEN, if openPosition is provided, it should match heroPosition
      if (config.openPosition && config.openPosition !== config.heroPosition) {
        console.error('For OPEN bet type: If openPosition is provided, it must match heroPosition', {
          heroPosition: config.heroPosition,
          openPosition: config.openPosition
        });
        return false;
      }
      break;

    case BET_TYPES.FACING_OPEN_CALL:
      // For calls vs open, hero cannot be the opener
      if (config.heroPosition === config.openPosition) {
        console.error('For FACING_OPEN_CALL: Hero cannot be the opener', {
          heroPosition: config.heroPosition,
          openPosition: config.openPosition
        });
        return false;
      }
      break;

    case BET_TYPES.FACING_OPEN_3BET:
      // For 3bets, hero cannot be the opener
      if (config.heroPosition === config.openPosition) {
        console.error('For FACING_OPEN_3BET: Hero cannot be the opener', {
          heroPosition: config.heroPosition,
          openPosition: config.openPosition
        });
        return false;
      }
      // If threeBetPosition is provided, it should match heroPosition
      if (config.threeBetPosition && config.threeBetPosition !== config.heroPosition) {
        console.warn('For FACING_OPEN_3BET: If threeBetPosition is provided, it should match heroPosition', {
          heroPosition: config.heroPosition,
          threeBetPosition: config.threeBetPosition
        });
        // Just a warning, don't block execution
      }
      break;

    case BET_TYPES.FACING_3BET_CALL:
      // For calls vs 3bet, hero should be the original opener
      if (config.heroPosition !== config.openPosition) {
        console.log('For FACING_3BET_CALL: Hero should be the original opener, but found:', {
          heroPosition: config.heroPosition,
          openPosition: config.openPosition
        });
        // Set openPosition to match heroPosition to fix the scenario
        config.openPosition = config.heroPosition;
      }
      
      // 3bettor must be different from hero
      if (config.heroPosition === config.threeBetPosition) {
        console.error('For FACING_3BET_CALL: Hero cannot be the 3bettor', {
          heroPosition: config.heroPosition,
          threeBetPosition: config.threeBetPosition
        });
        return false;
      }
      break;

      case BET_TYPES.FACING_3BET_4BET:
        // For 4bets, only validate that hero cannot be the 3bettor
        if (config.heroPosition === config.threeBetPosition) {
          console.error('For FACING_3BET_4BET: Hero cannot be the 3bettor', {
            heroPosition: config.heroPosition,
            threeBetPosition: config.threeBetPosition
          });
          return false;
        }
        
        // In 4bet scenarios, if openPosition is missing or empty, set a default
        if (!config.openPosition) {
          console.log('For FACING_3BET_4BET: No opener position provided, setting hero as opener');
          config.openPosition = config.heroPosition; // Hero is typically the opener in 4bet scenarios
        }
      // Only validate opener relationships if openPosition is provided
      else if (config.openPosition === config.threeBetPosition) {
        console.error('For FACING_3BET_4BET: Opener cannot be the 3bettor', {
          openPosition: config.openPosition,
          threeBetPosition: config.threeBetPosition
        });
        return false;
      }
      break;

    default:
      // Unknown bet type, just warn
      console.warn('Unknown bet type for position validation:', config.betType);
  }

  // Position order validation for 3bet scenarios
  if ((config.betType === BET_TYPES.FACING_3BET_CALL || 
       config.betType === BET_TYPES.FACING_3BET_4BET) && 
      config.threeBetPosition && 
      config.openPosition && 
      config.threeBetPosition !== config.openPosition) {
      
    // Standard position order
    const positionOrder = {
      'LJ': 0, 'HJ': 1, 'CO': 2, 'BTN': 3, 'SB': 4, 'BB': 5
    };
    
    const openPos = positionOrder[config.openPosition];
    const threeBetPos = positionOrder[config.threeBetPosition];
    
    // 3bettor should typically act after opener, just log a warning if not
    if (openPos > threeBetPos) {
      console.warn('Unusual 3bet scenario: 3bettor acts before opener', {
        opener: config.openPosition, 
        threeBettor: config.threeBetPosition
      });
      // Don't block execution, just warn
    }
  }

  // All validations passed
  console.log('Game config validation passed:', config);
  return true;
};

// Clean implementation of calculateBetAmount
const calculateBetAmount = (position, gameConfig) => {
  if (!position || !gameConfig) return 0;
  
  const { betType, openPosition, threeBetPosition, heroPosition } = gameConfig;
  let bet = 0;

  // Set basic blind amounts
  if (position === 'BB') bet = 1;
  if (position === 'SB') bet = 0.5;
  
  // Handle standard open with special SB sizing (3bb instead of 2.5bb)
  if (position === openPosition) {
    if (position === 'SB') {
      // Special case: SB opens to 3bb
      bet = Math.max(bet, 3);
    } else {
      // Standard 2.5bb open for all other positions
      bet = Math.max(bet, 2.5);
    }
  }
  
  // Handle 3bets with position-based sizing (unchanged)
  if (position === threeBetPosition && 
      (betType === 'FACING_3BET_4BET' || betType === 'FACING_3BET_CALL') &&
      position !== openPosition) {
    
    // Calculate 3bet size based on position
    bet = get3BetSize(threeBetPosition, openPosition);
  }
  
  // Handle hero 3betting (unchanged)
  if (position === heroPosition && betType === 'FACING_OPEN_3BET') {
    // Calculate 3bet size based on position
    bet = get3BetSize(position, openPosition);
  }

  return bet;
};

const checkPositionRelationship = (position1, position2) => {
  // Standard poker positions in order of acting (earlier = worse position)
  const positionOrder = {
    'LJ': 0,
    'HJ': 1, 
    'CO': 2,
    'BTN': 3,
    'SB': 4,
    'BB': 5
  };
  
  // Get numeric position values
  const pos1Value = positionOrder[position1];
  const pos2Value = positionOrder[position2];
  
  // Special case: In preflop, SB/BB act before everyone initially, but after everyone post-flop
  // For preflop 3bet sizing purposes, acting later = in position
  const isPos1InPosition = pos1Value > pos2Value;
  
  return {
    isPos1InPosition, 
    isPos2InPosition: !isPos1InPosition,
    pos1ActsFirst: pos1Value < pos2Value
  };
};

const calculateSeats = (gameConfig) => {
  if (!gameConfig?.heroPosition) return [];
  
  const { heroPosition, betType, openPosition, threeBetPosition } = gameConfig;
  
  // Standard poker table positions in clockwise order
  const allPositions = ['LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB'];
  
  // Find hero index in standard positions
  const heroIdx = allPositions.indexOf(heroPosition);
  if (heroIdx === -1) {
    console.error('Invalid hero position:', heroPosition);
    return [];
  }
  
  // Get the 5 positions clockwise from hero
  let orderedPositions = [];
  for (let i = 1; i <= 5; i++) {
    const posIndex = (heroIdx + i) % allPositions.length;
    orderedPositions.push(allPositions[posIndex]);
  }

  // Create the seat data with correct positions
  const seatPositions = [];
  for (let i = 0; i < 5; i++) {
    const position = orderedPositions[i];
    
    // Calculate bet amount
    let bet = 0;
    
    // Set basic blind amounts
    if (position === 'BB') bet = 1;
    if (position === 'SB') bet = 0.5;
    
    // Handle standard open with special SB sizing
    if (position === openPosition) {
      if (position === 'SB') {
        // Special case: SB opens to 3bb
        bet = Math.max(bet, 3);
      } else {
        // Standard 2.5bb open for all other positions
        bet = Math.max(bet, 2.5);
      }
    }
    
    // For 3bettors, calculate correct sizing based on position
    if (position === threeBetPosition && 
        (betType === 'FACING_3BET_4BET' || betType === 'FACING_3BET_CALL') &&
        position !== openPosition) {
      
      // Set correct 3bet size based on position
      bet = get3BetSize(threeBetPosition, openPosition);
      
      console.log(`Seat calculation: 3bet size for ${position} vs ${openPosition}: ${bet}bb`);
    }
    
    const stack = STARTING_STACK - bet;
    
    // Determine active state
    let isActive = false;
    if (betType === 'FACING_3BET_4BET' || betType === 'FACING_3BET_CALL') {
      isActive = position === threeBetPosition;
    } else if (betType === 'FACING_OPEN_CALL' || betType === 'FACING_OPEN_3BET') {
      isActive = position === openPosition;
    } else if (betType === 'OPEN') {
      // For open scenarios, positions after hero are active
      const { pos1ActsFirst } = checkPositionRelationship(position, heroPosition);
      isActive = !pos1ActsFirst;
    }

    seatPositions.push({
      id: `seat-${i+1}`,
      className: `seat-${i+1}`,
      title: position,
      blinds: stack.toFixed(1),
      isDealer: position === 'BTN',
      bet,
      isActive,
      // Add this line to pass gameConfig to each seat
      gameConfig
    });
  }

  return seatPositions;
};

// Helper function for range title
const RangeTitle = ({ gameConfig }) => {
  if (!gameConfig) return null;
  
  const { heroPosition, betType, openPosition, threeBetPosition } = gameConfig;
  
  if (!heroPosition || !betType) {
    return <div className="range-title">Range</div>;
  }

  let title = '';
  
  if (betType === 'FACING_3BET_4BET') {
    title = `${heroPosition} 4Bet vs ${threeBetPosition} 3Bet Range`;
  } else if (betType === 'FACING_3BET_CALL') {
    title = `${heroPosition} Call vs ${threeBetPosition} 3Bet Range`;
  } else if (betType === 'FACING_OPEN_3BET') {
    title = `${heroPosition} 3Bet vs ${openPosition} Open Range`;
  } else if (betType === 'FACING_OPEN_CALL') {
    title = `${heroPosition} Call vs ${openPosition} Open Range`;
  } else if (betType === 'OPEN') {
    title = `${heroPosition} Open Range`;
  } else if (betType === 'SB_LIMP' || betType === 'LIMP' || 
            (heroPosition === 'SB' && (betType || '').toUpperCase().includes('LIMP'))) {
    title = `SB Limp Range`;
  } else {
    title = `${heroPosition} ${betType} Range`;
  }

  return <div className="range-title">{title}</div>;
};

const get3BetSize = (threeBetPosition, openPosition) => {
  // Error handling for invalid inputs
  if (!threeBetPosition) {
    console.error(`Missing 3bettor position`);
    return 10; // Default to larger size in error case
  }
  
  // Fallback for missing opener in 4bet scenarios
  // When BTN is 3betting, assume they're in position vs any missing opener
  if (!openPosition) {
    console.log(`Missing opener position for 3bettor ${threeBetPosition}, using fallback`);
    // BTN, SB are typically in position vs earlier positions
    if (threeBetPosition === 'BTN' || threeBetPosition === 'SB') {
      console.log(`${threeBetPosition} is typically in position, using 7.5bb sizing`);
      return 7.5;
    } else {
      // Other positions are typically out of position
      console.log(`${threeBetPosition} is typically out of position, using 10bb sizing`);
      return 10;
    }
  }
  
  // Error check: Cannot 3bet yourself
  if (threeBetPosition === openPosition) {
    console.error(`3bettor ${threeBetPosition} cannot be the same as opener ${openPosition}`);
    return 10; // Default to larger size in error case
  }
  
  // Determine if 3bettor is in position relative to opener
  const is3BettorInPosition = isInPosition(threeBetPosition, openPosition);
  
  // Log for debugging
  console.log(`3bet size calculation: ${threeBetPosition} vs ${openPosition} = ${is3BettorInPosition ? '7.5bb (in position)' : '10bb (out of position)'}`);
  
  // Use proper 3bet sizing based on position
  // In position = 7.5bb, Out of position = 10bb
  return is3BettorInPosition ? 7.5 : 10;
};

// Get question text based on config
const getQuestionText = (gameConfig) => {
  if (!gameConfig) return "";
  
  const { betType, openPosition, threeBetPosition, heroPosition } = gameConfig;
  
  // Special case for SB limp
  if (betType === 'SB_LIMP' || betType === 'LIMP' || 
      (heroPosition === 'SB' && (betType || '').toUpperCase().includes('LIMP'))) {
    return "What frequency are you limping?";
  }
  
  switch (betType) {
    case BET_TYPES.OPEN:
      return "What frequency are you opening?";
    case BET_TYPES.FACING_OPEN_CALL:
      return `The ${openPosition} opened, what percent do you call with this hand?`;
    case BET_TYPES.FACING_OPEN_3BET:
      return `The ${openPosition} opened, what percent do you 3bet this hand?`;
    case BET_TYPES.FACING_3BET_CALL:
      return `The ${threeBetPosition} 3bet, what percent do you call this hand?`;
    case BET_TYPES.FACING_3BET_4BET:
      return `The ${threeBetPosition} 3bet, what percent do you 4bet this hand?`;
    default:
      return "What frequency are you playing this hand?";
  }
};

// Calculate hero bet and blinds
const getHeroBetAndBlinds = (gameConfig) => {
  if (!gameConfig) return { bet: 0, blinds: STARTING_STACK.toFixed(1) };
  
  const { heroPosition, betType, openPosition } = gameConfig;
  // Calculate bet amount for hero - with special SB sizing
  let heroBet = 0;
  
  // Set basic blind amounts
  if (heroPosition === 'BB') heroBet = 1;
  if (heroPosition === 'SB') heroBet = 0.5;
  
  // For open scenarios:
  if (betType === 'OPEN') {
    if (heroPosition === 'SB') {
      // Special case: SB opens to 3bb
      heroBet = 3;
    } else {
      // Standard 2.5bb open for all other positions
      heroBet = 2.5;
    }
  }
  
  // For 3bet/4bet scenarios when hero is opener:
  if ((betType === 'FACING_3BET_CALL' || betType === 'FACING_3BET_4BET') && 
      heroPosition === openPosition) {
    if (heroPosition === 'SB') {
      // Special case: SB opens to 3bb
      heroBet = 3;
    } else {
      // Standard 2.5bb open for other positions
      heroBet = 2.5;
    }
  }
  
  const remainingBlinds = (STARTING_STACK - heroBet).toFixed(1);
  
  return {
    bet: heroBet,
    blinds: remainingBlinds
  };
};

const PreflopTable = () => {
  const navigate = useNavigate();
  const {
    checkAnswer,
    gameState,
    quizAnalytics,
    getNextHandDirective,
    commitHandDirective,
    markHandShown,
    markHandResult
  } = useGame();
  const { generateHand } = useHandGenerator();

  // Full hand-class string for a dealt hand, e.g. "AKs", "77", "A5o".
  // heroCards is always ordered high-rank-first (see orderCardsByRank).
  const handToClassString = useCallback((hand) => {
    if (!hand?.card1Value || !hand?.card2Value) return null;
    if (hand.card1Value === hand.card2Value) return hand.card1Value + hand.card2Value;
    const suited = hand.card1Suit === hand.card2Suit;
    return hand.card1Value + hand.card2Value + (suited ? 's' : 'o');
  }, []);

  // State declarations
  const [seats, setSeats] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [heroCards, setHeroCards] = useState(null);
  const [currentQuizResult, setCurrentQuizResult] = useState(null);

  // ── Hand intro animation ──────────────────────────────────────────────────
  // The animation runs once per hand before the action buttons appear.
  // It is enabled when gameConfig.showHandIntro === true.
  // Reset whenever questionCount advances (new hand dealt).
  const [animationComplete, setAnimationComplete] = useState(false);
  useEffect(() => {
    setAnimationComplete(false);
  }, [gameState.questionCount]);

  // Get game config from context
  const gameConfig = useMemo(() => gameState?.gameConfig, [gameState?.gameConfig]);

  // NOTE: hand-repeat tracking (which hand classes have been shown this
  // quiz) lives in GameContext, not a local ref here — this component
  // unmounts/remounts on every question (navigates to /answer and back), so a
  // local ref would lose its history each time. See getNextHandDirective /
  // markHandShown / markHandResult from useGame().

  // Get range title from context
  const rangeTitle = useMemo(() => {
    return gameState?.rangeTitle || gameConfig?.rangeTitle || "";
  }, [gameState?.rangeTitle, gameConfig?.rangeTitle]);

  // Validate config and redirect if invalid
  useEffect(() => {
    if (!gameConfig || !validateGameConfig(gameConfig)) {
      console.error('Invalid game configuration, redirecting to config');
      navigate('/quiz-config');
    }
  }, [gameConfig, navigate]);

  // Skip rendering if config is invalid
  if (!gameConfig || !validateGameConfig(gameConfig)) {
    return null;
  }

  // Initialize quiz and load hand
  useEffect(() => {
    let isMounted = true;
  
    const initializeAndLoad = async () => {
      try {
        // Initialize quiz result if needed
        if (!currentQuizResult && gameConfig) {
          console.log('Creating new quiz result with config:', gameConfig);
          const newQuizResult = new QuizResult(gameConfig);
          if (isMounted) {
            setCurrentQuizResult(newQuizResult);
          }
        }
  
        // Check if we should load a new hand
        if (!gameConfig || gameState.questionCount >= gameConfig.totalHands) {
          return;
        }
  
        setIsProcessing(true);

        // Pull a forced-return hand (a wrong answer coming due) plus the
        // current ban set, and generate the next hand accordingly.
        const { forcedHandClass, usedHandKeys } = getNextHandDirective();
        const { hand } = await generateHand(
          gameConfig.betType,
          gameConfig.range,
          gameConfig.heroPosition,
          gameConfig.threeBetPosition,
          usedHandKeys,
          forcedHandClass
        );

        if (hand && isMounted) {
          // Record this hand class so it won't be dealt again this quiz
          const handClass = handToClassString(hand);
          if (handClass) {
            markHandShown(handClass);
          }
          // Only now that we're committing to actually rendering this hand
          // do we remove it from the retry queue — see commitHandDirective
          // in GameContext for why this can't happen at getNextHandDirective
          // (peek) time.
          if (forcedHandClass) {
            commitHandDirective(forcedHandClass);
          }
          setHeroCards(hand);
          // Calculate seats with correct bet amounts
          setSeats(calculateSeats(gameConfig));
        }
      } catch (error) {
        console.error('Error in initialization:', error);
      } finally {
        if (isMounted) {
          setIsProcessing(false);
        }
      }
    };

    initializeAndLoad();

    return () => {
      isMounted = false;
    };
  }, [gameConfig, gameState.questionCount, generateHand, getNextHandDirective, commitHandDirective, markHandShown, handToClassString]);

  useEffect(() => {
    if (!gameConfig) return;
    
    const newSeats = calculateSeats(gameConfig);
    
    // Debug the 3bettor seat specifically
    if (gameConfig.threeBetPosition) {
      const threeBettorSeat = newSeats.find(seat => seat.title === gameConfig.threeBetPosition);
      if (threeBettorSeat) {
        const positionOrder = {'LJ': 0, 'HJ': 1, 'CO': 2, 'BTN': 3, 'SB': 4, 'BB': 5};
        const is3BettorInPosition = positionOrder[gameConfig.threeBetPosition] > positionOrder[gameConfig.openPosition];
        const expected3BetSize = is3BettorInPosition ? 7.5 : 10;
        
        console.log('SEAT CALCULATION DEBUG:', {
          threeBettorPosition: gameConfig.threeBetPosition,
          openPosition: gameConfig.openPosition,
          inPosition: is3BettorInPosition,
          calculatedBet: threeBettorSeat.bet,
          expectedBet: expected3BetSize,
          match: threeBettorSeat.bet === expected3BetSize
        });
        
        // Ensure the bet is correct in the seat data
        if (threeBettorSeat.bet !== expected3BetSize) {
          console.log('Correcting 3bettor bet size in seat data');
          threeBettorSeat.bet = expected3BetSize;
        }
      }
    }
    
    setSeats(newSeats);
  }, [gameConfig]);
  
  // Handle action callback
  const handleAction = useCallback(async (action) => {
    if (!heroCards || !gameConfig || isProcessing) {
      return;
    }
  
    setIsProcessing(true);
    try {
      // Add heroPosition to hand
      const handWithPosition = {
        ...heroCards,
        heroPosition: gameConfig.heroPosition
      };
      
      const chosenFrequency = parseInt(action.split('_')[0], 10);
      
      // Process answer
      const result = await checkAnswer(handWithPosition, action, gameConfig.range);

      // Wrong answers are forced back in a couple hands; correct answers
      // stay banned for the rest of the quiz (see GameContext hand tracking).
      const answeredHandClass = handToClassString(heroCards);
      if (answeredHandClass) {
        markHandResult(answeredHandClass, result.isCorrect);
      }

      // Determine action type for display
      const getActionTypeDisplay = (betType) => {
        // Check for LIMP scenarios
        if (betType === 'LIMP' || betType === 'SB_LIMP') {
          return 'LIMP';
        }
        
        switch (betType) {
          case BET_TYPES.FACING_3BET_4BET:
            return '4BET';
          case BET_TYPES.FACING_OPEN_3BET:
            return '3BET';
          case BET_TYPES.FACING_OPEN_CALL:
          case BET_TYPES.FACING_3BET_CALL:
            return 'CALL';
          default:
            return 'OPEN';
        }
      };
  
      // Navigate to answer screen
      const isLastQuestion = gameState.questionCount + 1 >= gameConfig.totalHands;
      navigate('/answer', {
        state: {
          hand: heroCards,
          chosenFrequency,
          actualFrequency: result.actualFrequency,
          isCorrect: result.isCorrect,
          isLastQuestion,
          betType: gameConfig.betType,
          actionTypeDisplay: getActionTypeDisplay(gameConfig.betType),
          heroPosition: gameConfig.heroPosition,
          threeBetPosition: gameConfig.threeBetPosition,
          config: {
            ...gameConfig,
            range: gameConfig.range
          }
        }
      });
    } catch (error) {
      console.error('Error in handleAction:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [heroCards, gameConfig, checkAnswer, isProcessing, gameState, navigate, markHandResult, handToClassString]);

  // Button generation helpers
  const createButton = useCallback((percentage, actionType, buttonType) => {
    const numericPercentage = parseInt(percentage, 10);
    
    return {
      text: `${percentage}%`,
      buttonLargeClassName: `${buttonType}-button ${isProcessing ? 'opacity-50' : ''}`,
      onClick: () => handleAction(`${numericPercentage}_${actionType.toLowerCase()}`)
    };
  }, [isProcessing, handleAction]);

  const getPercentageButtons = useCallback((actionType) => {
    return [
      createButton('100', actionType, 'hundred'),
      createButton('50', actionType, 'fifty'),
      createButton('0', actionType, 'zero')
    ];
  }, [createButton]);

  const getButtons = (config) => {
    if (!config) return [];
  
    // Special case for SB limp
    if (config.betType === 'SB_LIMP' || config.betType === 'LIMP' || 
        (config.heroPosition === 'SB' && (config.betType || '').toUpperCase().includes('LIMP'))) {
      return getPercentageButtons('Limp');
    }
  
    switch (config.betType) {
      case BET_TYPES.OPEN:
        return getPercentageButtons('Open');
      case BET_TYPES.FACING_OPEN_CALL:
      case BET_TYPES.FACING_3BET_CALL:
      case BET_TYPES.FACING_4BET_CALL:
        return getPercentageButtons('Call');
      case BET_TYPES.FACING_OPEN_3BET:
        return getPercentageButtons('3Bet');
      case BET_TYPES.FACING_3BET_4BET:
      case BET_TYPES.SQUEEZE_4BET:
        return getPercentageButtons('4Bet');
      default:
        return getPercentageButtons('Open'); // Default to Open
    }
  };

  // Memoized values
  const buttons = useMemo(() => {
    return getButtons(gameConfig);
  }, [gameConfig, getButtons]);

  const calculatedSeats = useMemo(() => {
    if (!gameConfig) return [];
    return calculateSeats(gameConfig);
  }, [gameConfig]);

  const questionText = useMemo(() => {
    return getQuestionText(gameConfig);
  }, [gameConfig]);

  const heroBetAndBlinds = useMemo(() => {
    if (!gameConfig) return { bet: 0, blinds: STARTING_STACK.toFixed(1) };
    return getHeroBetAndBlinds(gameConfig);
  }, [gameConfig]);

  // Fix hero bet for specific scenarios
  let fixedBet = heroBetAndBlinds.bet;
  let fixedBlinds = heroBetAndBlinds.blinds;

  // Critical fix: Always use 2.5bb when hero is the opener in a 3bet/4bet scenario
  if (gameConfig && 
      (gameConfig.betType === 'FACING_3BET_4BET' || gameConfig.betType === 'FACING_3BET_CALL') && 
      gameConfig.heroPosition === gameConfig.openPosition) {
      fixedBet = 2.5;
      fixedBlinds = (STARTING_STACK - 2.5).toFixed(1);
  }

  // Determine whether the hand intro animation should run for this hand.
  // Enabled when gameConfig.showHandIntro is true AND the animation hasn't finished yet.
  const showIntroAnimation = Boolean(gameConfig?.showHandIntro) && !animationComplete;

  return (
    <div className="table">
      <GameHeader
        currentHand={gameState.questionCount + 1}
        totalHands={gameConfig.totalHands}
        onCancel={() => navigate('/quiz-config')}
      />

      <div className="table-wrapper">
        <div className="div" />
        <RangeTitle gameConfig={gameConfig} />

        {seats.map(seat => (
          <Seat
            key={`${seat.id}-${gameState.questionCount}-${seat.title}`}
            {...seat}
            gameConfig={gameConfig}
          />
        ))}

        <div className="hero-seat-wrapper">
          <HeroSeat
            id="hero"
            title={gameConfig.heroPosition}
            bet={fixedBet}
            blinds={fixedBlinds}
            card1Value={heroCards?.card1Value}
            card1Suit={heroCards?.card1Suit}
            card2Value={heroCards?.card2Value}
            card2Suit={heroCards?.card2Suit}
            betType={gameConfig.betType}
            openPosition={gameConfig.openPosition}
            isHeroOpener={gameConfig.heroPosition === gameConfig.openPosition}
            gameConfig={gameConfig}
          />
        </div>

        {/* Hand intro animation overlay — sits inside table-wrapper, above seats */}
        {showIntroAnimation && (
          <HandIntroAnimation
            key={gameState.questionCount} /* remount on each new hand */
            gameConfig={gameConfig}
            heroCards={heroCards}
            onComplete={() => setAnimationComplete(true)}
          />
        )}
      </div>

      {/* Question text and action buttons are hidden until the animation finishes */}
      {(!gameConfig?.showHandIntro || animationComplete) && (
        <>
          <Question text={questionText} />
          <BottomControls buttons={buttons} />
        </>
      )}
    </div>
  );
};

export default React.memo(PreflopTable);